import { useState } from "react";
import { Modal } from "./common/Modal";
import { Button } from "./common/Button";
import { Input } from "./common/Input";
import { useUpdateProject } from "../hooks/useProjects";
import { useProjectContext } from "../contexts/ProjectContext";

interface ProjectSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
}

// Exported type for Dashboard to use
export interface ParsedSetupData {
  _version: number;
  _type: string;
  phase: string;
  property: {
    price: number | null;
    address: string | null;
    type: string | null;
    size: number | null;
  };
  renovation: {
    type: string | null;
    budget: number | null;
    financing: string | null;
  };
  timeline: {
    possession_date: string | null;
    move_in_target: string | null;
  };
  priorities: string[];
  notes: string | null;
}

// Helper to parse setup data from description
export const parseSetupData = (
  description: string | null | undefined,
): ParsedSetupData | null => {
  if (!description) return null;
  try {
    const parsed = JSON.parse(description);
    if (parsed._type === "project_setup") {
      return parsed as ParsedSetupData;
    }
  } catch {
    // Not JSON, return null
  }
  return null;
};

const PROJECT_PHASES = [
  {
    id: "ricerca",
    label: "Ricerca immobile",
    description: "Sto cercando casa",
    icon: "🔍",
  },
  {
    id: "trattativa",
    label: "Trattativa/Proposta",
    description: "Ho trovato casa, sto trattando",
    icon: "🤝",
  },
  {
    id: "preliminare",
    label: "Preliminare firmato",
    description: "Compromesso fatto, attendo rogito",
    icon: "📝",
  },
  {
    id: "rogito",
    label: "Rogito fatto",
    description: "Casa mia! Devo ristrutturare",
    icon: "🔑",
  },
  {
    id: "progettazione",
    label: "Progettazione",
    description: "Sto progettando i lavori",
    icon: "📐",
  },
  {
    id: "preventivi",
    label: "Raccolta preventivi",
    description: "Sto raccogliendo preventivi",
    icon: "📋",
  },
  {
    id: "lavori",
    label: "Lavori in corso",
    description: "Cantiere aperto",
    icon: "🏗️",
  },
  {
    id: "finishing",
    label: "Finiture",
    description: "Ultimi dettagli e arredi",
    icon: "🎨",
  },
  {
    id: "completato",
    label: "Completato",
    description: "Progetto concluso",
    icon: "✅",
  },
];

export const getPhaseLabel = (phaseId: string): string => {
  const phase = PROJECT_PHASES.find((p) => p.id === phaseId);
  return phase ? `${phase.icon} ${phase.label}` : phaseId;
};

const PROPERTY_TYPES = [
  "Appartamento",
  "Villa/Casa indipendente",
  "Villetta a schiera",
  "Loft/Open space",
  "Attico",
  "Altro",
];

const RENOVATION_TYPES = [
  "Ristrutturazione completa",
  "Ristrutturazione parziale",
  "Solo impianti",
  "Solo finiture",
  "Manutenzione straordinaria",
  "Nuova costruzione",
];

const PRIORITY_OPTIONS = [
  "Risparmio energetico",
  "Comfort abitativo",
  "Estetica/Design",
  "Funzionalita spazi",
  "Sicurezza",
  "Domotica",
  "Outdoor/Giardino",
  "Rispetto budget",
  "Velocita lavori",
];

const FINANCING_OPTIONS = [
  { value: "no", label: "No, pago in contanti" },
  { value: "mutuo", label: "Si, mutuo per acquisto" },
  { value: "prestito", label: "Si, prestito per lavori" },
  { value: "entrambi", label: "Si, mutuo + prestito lavori" },
];

export function ProjectSetupModal({
  isOpen,
  onClose,
  projectId,
  projectName,
}: ProjectSetupModalProps) {
  const { currentProject } = useProjectContext();
  const [step, setStep] = useState(0);
  const totalSteps = 4; // 0: Phase, 1: Property, 2: Renovation, 3: Timeline, 4: Priorities

  const updateProject = useUpdateProject();

  const [data, setData] = useState({
    current_phase: "",
    description: "",
    start_date: "",
    end_date: "",
    property_price: "",
    property_address: "",
    property_type: "",
    property_size: "",
    renovation_type: "",
    estimated_budget: "",
    financing_needed: "",
    possession_date: "",
    move_in_target: "",
    priorities: [] as string[],
  });

  const updateField = (field: string, value: string | string[]) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const togglePriority = (priority: string) => {
    setData((prev) => ({
      ...prev,
      priorities: prev.priorities.includes(priority)
        ? prev.priorities.filter((p) => p !== priority)
        : [...prev.priorities, priority],
    }));
  };

  const handleSave = async () => {
    try {
      const setupData: ParsedSetupData = {
        ...(currentProject?.id === projectId
          ? parseSetupData(currentProject.description)
          : null),
        _version: 1,
        _type: "project_setup",
        phase: data.current_phase,
        property: {
          price: data.property_price ? Number(data.property_price) : null,
          address: data.property_address || null,
          type: data.property_type || null,
          size: data.property_size ? Number(data.property_size) : null,
        },
        renovation: {
          type: data.renovation_type || null,
          budget: data.estimated_budget ? Number(data.estimated_budget) : null,
          financing: data.financing_needed || null,
        },
        timeline: {
          possession_date: data.possession_date || null,
          move_in_target: data.move_in_target || null,
        },
        priorities: data.priorities,
        notes: data.description || null,
      };

      await updateProject.mutateAsync({
        id: projectId,
        description: JSON.stringify(setupData),
      });

      onClose();
    } catch (err) {
      console.error("Error saving project setup:", err);
      alert("Errore nel salvataggio");
    }
  };

  const stepLabels = ["Fase", "Immobile", "Lavori", "Date", "Priorita"];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Setup: ${projectName}`}>
      <div className="space-y-6">
        {/* Progress indicator */}
        <div className="flex items-center justify-between mb-4">
          {stepLabels.map((label, idx) => (
            <div key={idx} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    idx === step
                      ? "bg-primary-600 text-white"
                      : idx < step
                        ? "bg-green-500 text-white"
                        : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {idx < step ? "✓" : idx + 1}
                </div>
                <span className="text-xs mt-1 text-gray-500">{label}</span>
              </div>
              {idx < stepLabels.length - 1 && (
                <div
                  className={`w-8 h-1 mx-1 ${
                    idx < step ? "bg-green-500" : "bg-gray-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 0: Project Phase */}
        {step === 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              In che fase sei?
            </h3>
            <p className="text-sm text-gray-600">
              Seleziona la fase attuale del tuo progetto
            </p>

            <div className="grid grid-cols-1 gap-2 max-h-80 overflow-y-auto">
              {PROJECT_PHASES.map((phase) => (
                <button
                  key={phase.id}
                  type="button"
                  onClick={() => updateField("current_phase", phase.id)}
                  className={`p-4 text-left rounded-lg border transition-all ${
                    data.current_phase === phase.id
                      ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{phase.icon}</span>
                    <div>
                      <div className="font-medium text-gray-900">
                        {phase.label}
                      </div>
                      <div className="text-sm text-gray-500">
                        {phase.description}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Property Info */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Informazioni Immobile
            </h3>
            <p className="text-sm text-gray-600">
              Inserisci i dettagli dell’immobile (opzionale)
            </p>

            <Input
              label="Prezzo di acquisto (€)"
              type="number"
              value={data.property_price}
              onChange={(e) => updateField("property_price", e.target.value)}
              placeholder="250000"
            />

            <Input
              label="Indirizzo"
              type="text"
              value={data.property_address}
              onChange={(e) => updateField("property_address", e.target.value)}
              placeholder="Via Roma 123, Milano"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo immobile
              </label>
              <select
                value={data.property_type}
                onChange={(e) => updateField("property_type", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Seleziona...</option>
                {PROPERTY_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Superficie (mq)"
              type="number"
              value={data.property_size}
              onChange={(e) => updateField("property_size", e.target.value)}
              placeholder="100"
            />
          </div>
        )}

        {/* Step 2: Renovation Info */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Tipo di Intervento
            </h3>
            <p className="text-sm text-gray-600">
              Definisci il tipo di lavori e il budget
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo di ristrutturazione
              </label>
              <select
                value={data.renovation_type}
                onChange={(e) => updateField("renovation_type", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Seleziona...</option>
                {RENOVATION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Budget stimato lavori (€)"
              type="number"
              value={data.estimated_budget}
              onChange={(e) => updateField("estimated_budget", e.target.value)}
              placeholder="50000"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Finanziamento
              </label>
              <select
                value={data.financing_needed}
                onChange={(e) =>
                  updateField("financing_needed", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Seleziona...</option>
                {FINANCING_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Note progetto"
              type="text"
              value={data.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="Breve descrizione..."
            />
          </div>
        )}

        {/* Step 3: Timeline */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Tempistiche</h3>
            <p className="text-sm text-gray-600">
              Quando vuoi iniziare e finire?
            </p>

            <Input
              label="Data rogito/possesso"
              type="date"
              value={data.possession_date}
              onChange={(e) => updateField("possession_date", e.target.value)}
            />

            <Input
              label="Data inizio lavori"
              type="date"
              value={data.start_date}
              onChange={(e) => updateField("start_date", e.target.value)}
            />

            <Input
              label="Data fine lavori prevista"
              type="date"
              value={data.end_date}
              onChange={(e) => updateField("end_date", e.target.value)}
            />

            <Input
              label="Data target trasferimento"
              type="date"
              value={data.move_in_target}
              onChange={(e) => updateField("move_in_target", e.target.value)}
            />
          </div>
        )}

        {/* Step 4: Priorities */}
        {step === 4 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Le tue priorita
            </h3>
            <p className="text-sm text-gray-600">
              Cosa conta di piu per te? (seleziona max 3-4)
            </p>

            <div className="grid grid-cols-2 gap-2">
              {PRIORITY_OPTIONS.map((priority) => (
                <button
                  key={priority}
                  type="button"
                  onClick={() => togglePriority(priority)}
                  className={`p-3 text-left text-sm rounded-lg border transition-colors ${
                    data.priorities.includes(priority)
                      ? "border-primary-500 bg-primary-50 text-primary-700 font-medium"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {data.priorities.includes(priority) ? "✓ " : ""}
                  {priority}
                </button>
              ))}
            </div>

            {/* Summary */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm">
              <h4 className="font-medium mb-3">Riepilogo progetto:</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-gray-600">
                {data.current_phase && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Fase:</span>{" "}
                    {getPhaseLabel(data.current_phase)}
                  </div>
                )}
                {data.property_price && (
                  <div>
                    <span className="text-gray-500">Prezzo:</span> €
                    {Number(data.property_price).toLocaleString()}
                  </div>
                )}
                {data.property_type && (
                  <div>
                    <span className="text-gray-500">Tipo:</span>{" "}
                    {data.property_type}
                  </div>
                )}
                {data.property_size && (
                  <div>
                    <span className="text-gray-500">Mq:</span>{" "}
                    {data.property_size}
                  </div>
                )}
                {data.renovation_type && (
                  <div>
                    <span className="text-gray-500">Lavori:</span>{" "}
                    {data.renovation_type}
                  </div>
                )}
                {data.estimated_budget && (
                  <div>
                    <span className="text-gray-500">Budget:</span> €
                    {Number(data.estimated_budget).toLocaleString()}
                  </div>
                )}
                {data.start_date && (
                  <div>
                    <span className="text-gray-500">Inizio:</span>{" "}
                    {data.start_date}
                  </div>
                )}
                {data.end_date && (
                  <div>
                    <span className="text-gray-500">Fine:</span> {data.end_date}
                  </div>
                )}
              </div>
              {data.priorities.length > 0 && (
                <div className="mt-2 pt-2 border-t">
                  <span className="text-gray-500">Priorita:</span>{" "}
                  {data.priorities.join(", ")}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            type="button"
            variant="secondary"
            onClick={() => (step > 0 ? setStep(step - 1) : onClose())}
          >
            {step > 0 ? "Indietro" : "Salta"}
          </Button>

          {step < totalSteps ? (
            <Button type="button" onClick={() => setStep(step + 1)}>
              Avanti
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSave}
              disabled={updateProject.isPending}
            >
              {updateProject.isPending ? "Salvataggio..." : "Salva e inizia"}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
